/**
 * Version utility for EQUIPR
 * 
 * Reads version from package.json and optionally includes git commit hash
 * for non-production environments (staging, preview, development)
 * 
 * Usage:
 *   import { getVersion, getFullVersion } from '../lib/version';
 *   
 *   getVersion()     // "1.0.0"
 *   getFullVersion() // "1.0.0-abc1234" (staging) or "1.0.0" (production)
 */

// Version from package.json (injected at build time via React's default behavior)
const packageVersion = process.env.REACT_APP_VERSION || require('../../package.json').version;

// Git commit hash (set during build in CI/CD)
const gitHash = process.env.REACT_APP_GIT_HASH || '';

// Environment detection
const isProduction = process.env.NODE_ENV === 'production' && 
  !window.location.hostname.includes('preview') && 
  !window.location.hostname.includes('staging') &&
  !window.location.hostname.includes('localhost');

/**
 * Get the base version number
 * @returns {string} Version like "1.0.0"
 */
export const getVersion = () => packageVersion;

/**
 * Get the full version with git hash for non-production environments
 * @returns {string} Version like "1.0.0" (prod) or "1.0.0-abc1234" (staging)
 */
export const getFullVersion = () => {
  if (isProduction || !gitHash) {
    return packageVersion;
  }
  // Show short hash (first 7 characters) for staging/preview
  const shortHash = gitHash.substring(0, 7);
  return `${packageVersion}-${shortHash}`;
};

/**
 * Get version display string for UI
 * @param {boolean} collapsed - Whether sidebar is collapsed
 * @returns {string} Formatted version string
 */
export const getVersionDisplay = (collapsed = false) => {
  const version = getFullVersion();
  if (collapsed) {
    // Short format for collapsed sidebar: "v1.0" or "v1.0-abc"
    const parts = version.split('.');
    const shortVersion = `${parts[0]}.${parts[1]}`;
    const hash = version.includes('-') ? version.split('-')[1] : '';
    return hash ? `v${shortVersion.split('-')[0]}-${hash.substring(0, 3)}` : `v${shortVersion}`;
  }
  return `Version ${version}`;
};

/**
 * Get environment label
 * @returns {string} Environment name
 */
export const getEnvironment = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('localhost')) return 'LOCAL';
  if (hostname.includes('preview')) return 'PREVIEW';
  if (hostname.includes('staging')) return 'STAGING';
  return 'PROD';
};

export default {
  getVersion,
  getFullVersion,
  getVersionDisplay,
  getEnvironment
};
